import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, FileText, Loader2, ExternalLink, Trash2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import StatementPasswordReveal from "@/components/StatementPasswordReveal";
import GmailSyncPanel from "@/components/GmailSyncPanel";
import PrintPageButton from "@/components/PrintPageButton";
import StatementLedger from "@/components/StatementLedger";
import EmptyState from "@/components/EmptyState";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Statements() {
    const { hasRole } = useAuth();
    const [searchParams] = useSearchParams();
    const [cards, setCards] = useState([]);
    const [rows, setRows] = useState([]);
    const [cardFilter, setCardFilter] = useState("all");
    const [monthFilter, setMonthFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [upFile, setUpFile] = useState(null);
    const [upCard, setUpCard] = useState("");
    const [upMonth, setUpMonth] = useState(new Date().toISOString().slice(0, 7));
    const [runOcr, setRunOcr] = useState(true);
    // OCR provider — 'default' honours the admin-set default in
    // platform_settings; other values force a specific provider for this
    // single upload only.
    const [ocrModel, setOcrModel] = useState("default");
    const [existsWarning, setExistsWarning] = useState(null);
    const [manualFields, setManualFields] = useState({
        statement_date: "",
        due_date: "",
        total_amount_due: "",
        min_amount_due: "",
    });
    const [uploading, setUploading] = useState(false);
    const [review, setReview] = useState(null);
    const [pendingCount, setPendingCount] = useState(0);
    const fileRef = useRef();

    const load = useCallback(() => {
        const params = {};
        if (cardFilter !== "all") params.card_id = cardFilter;
        if (monthFilter) params.statement_month = monthFilter;
        api.get("/statements", { params }).then((r) => setRows(r.data));
    }, [cardFilter, monthFilter]);
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data));
        const preCard = searchParams.get("card");
        if (preCard) {
            setUpCard(preCard);
            setOpen(true);
        }
    }, [searchParams]);
    useEffect(() => { load(); }, [load]);

    // Keep a live count of pending-review statements — surfaced as a chip
    // in the header so Finance can jump into the OCR review queue.
    useEffect(() => {
        api.get("/statements/pending-review")
            .then((r) => setPendingCount(Array.isArray(r.data) ? r.data.length : 0))
            .catch(() => setPendingCount(0));
    }, [rows]);

    // Preemptive "statement already exists for this month" warning
    useEffect(() => {
        if (upCard && upMonth) {
            api.get("/statements/exists", { params: { card_id: upCard, statement_month: upMonth } })
                .then((r) => setExistsWarning(r.data.exists ? `A statement for ${upMonth} already exists for this card. Uploading will be blocked unless you mark it as revised.` : null))
                .catch(() => setExistsWarning(null));
        } else {
            setExistsWarning(null);
        }
    }, [upCard, upMonth]);

    const upload = async (e) => {
        e.preventDefault();
        if (!upFile || !upCard) return toast.error("Card and PDF file required");
        setUploading(true);
        const fd = new FormData();
        fd.append("file", upFile);
        fd.append("card_id", upCard);
        fd.append("statement_month", upMonth);
        fd.append("run_ocr", runOcr);
        // Only ship the override if the user explicitly picked one — leaving
        // it out lets the backend fall through to platform_settings.
        if (runOcr && ocrModel && ocrModel !== "default") {
            fd.append("ocr_model", ocrModel);
        }
        // If OCR is off, ship any manual values as form fields
        if (!runOcr) {
            if (manualFields.statement_date) fd.append("statement_date", manualFields.statement_date);
            if (manualFields.due_date) fd.append("due_date", manualFields.due_date);
            if (manualFields.total_amount_due) fd.append("total_amount_due", manualFields.total_amount_due);
            if (manualFields.min_amount_due) fd.append("min_amount_due", manualFields.min_amount_due);
        }
        try {
            const { data } = await api.post("/statements/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 120000,
            });
            toast.success(runOcr ? "Statement uploaded — review OCR extraction" : "Statement uploaded");
            setOpen(false);
            setUpFile(null);
            if (fileRef.current) fileRef.current.value = "";
            load();
            if (runOcr) setReview(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const saveReview = async () => {
        try {
            await api.patch(`/statements/${review.id}`, {
                statement_date: review.statement_date,
                due_date: review.due_date,
                period_from: review.period_from,
                period_to: review.period_to,
                total_amount_due: Number(review.total_amount_due || 0),
                min_amount_due: Number(review.min_amount_due || 0),
                opening_balance: Number(review.opening_balance || 0),
                purchases: Number(review.purchases || 0),
                payments: Number(review.payments || 0),
                interest: Number(review.interest || 0),
                late_fee: Number(review.late_fee || 0),
                gst: Number(review.gst || 0),
                reward_points_earned: Number(review.reward_points_earned || 0),
                reward_points_redeemed: Number(review.reward_points_redeemed || 0),
                transactions: review.transactions || [],
                status: "Reviewed",
            });
            toast.success("Statement data saved");
            setReview(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this statement? Only admins can delete.")) return;
        try {
            await api.delete(`/statements/${id}`);
            toast.success("Statement deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cardMap = Object.fromEntries(cards.map((c) => [c.id, c]));

    return (
        <div data-testid="statements-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow">Repository</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Statements.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3">
                        {rows.length} on file · PDFs stored securely
                    </p>
                </div>
                {hasRole("admin", "finance") && (
                    <div className="flex items-center gap-2">
                        <PrintPageButton />
                        {pendingCount > 0 && (
                            <Link
                                to="/statements/review"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-sm font-medium hover:bg-amber-100 transition-colors"
                                data-testid="stmt-review-chip"
                            >
                                <ShieldAlert size={13} />
                                {pendingCount} pending review
                            </Link>
                        )}
                        <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="upload-stmt-btn">
                                <Upload size={16} className="mr-1.5" /> Upload statement
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader><DialogTitle className="font-display text-2xl">Upload statement PDF</DialogTitle></DialogHeader>
                            <form onSubmit={upload} className="space-y-4">
                                <div>
                                    <Label className="eyebrow">Card</Label>
                                    <Select value={upCard} onValueChange={setUpCard}>
                                        <SelectTrigger className="mt-1.5" data-testid="stmt-card-select"><SelectValue placeholder="Select card" /></SelectTrigger>
                                        <SelectContent>
                                            {cards.map((c) => (
                                                <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                {existsWarning && (
                                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="stmt-exists-warning">
                                        ⚠ {existsWarning}
                                    </div>
                                )}
                                <div>
                                    <Label className="eyebrow">Statement Month</Label>
                                    <Input type="month" value={upMonth} onChange={(e) => setUpMonth(e.target.value)} className="mt-1.5" required data-testid="stmt-month" />
                                </div>
                                <div>
                                    <Label className="eyebrow">PDF File</Label>
                                    <Input ref={fileRef} type="file" accept="application/pdf" onChange={(e) => setUpFile(e.target.files[0])} className="mt-1.5" required data-testid="stmt-file" />
                                </div>
                                <div className="flex items-center justify-between border border-stone-200 rounded-md px-3 py-2.5">
                                    <div>
                                        <div className="text-sm">Auto-extract with OCR</div>
                                        <div className="text-[11px] text-stone-500">Uses Gemini to parse totals & transactions</div>
                                    </div>
                                    <Switch checked={runOcr} onCheckedChange={setRunOcr} data-testid="stmt-ocr-toggle" />
                                </div>
                                {runOcr && (
                                    <div className="border border-violet-200 rounded-md p-3 bg-violet-50/30" data-testid="stmt-ocr-model-picker">
                                        <div className="flex items-center justify-between gap-2">
                                            <Label className="eyebrow text-violet-900">OCR provider</Label>
                                            <span className="text-[10px] text-stone-500">Overrides the workspace default for this upload only</span>
                                        </div>
                                        <div className="grid grid-cols-3 gap-1.5 mt-2">
                                            {[
                                                { id: "default", label: "Use default",  hint: "Whatever the admin picked" },
                                                { id: "gemini",  label: "Gemini 2.5",   hint: "Fast · cheap · clean PDFs" },
                                                { id: "gpt",     label: "GPT 5.6 Sol",  hint: "Best on messy scans" },
                                            ].map((opt) => {
                                                const active = ocrModel === opt.id;
                                                return (
                                                    <button
                                                        key={opt.id}
                                                        type="button"
                                                        onClick={() => setOcrModel(opt.id)}
                                                        className={`text-left rounded-md border p-2 transition-all ${
                                                            active
                                                                ? "border-violet-500 bg-white ring-1 ring-violet-300"
                                                                : "border-stone-200 bg-white hover:border-stone-400"
                                                        }`}
                                                        data-testid={`stmt-ocr-model-${opt.id}`}
                                                    >
                                                        <div className="text-[12px] font-semibold text-stone-900">{opt.label}</div>
                                                        <div className="text-[10.5px] text-stone-500 mt-0.5 leading-tight">{opt.hint}</div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {!runOcr && (
                                    <div className="grid grid-cols-2 gap-3 border border-stone-200 rounded-md p-3">
                                        <div className="col-span-2 eyebrow">Manual entry (optional)</div>
                                        <div>
                                            <Label className="eyebrow">Statement Date</Label>
                                            <Input type="date" value={manualFields.statement_date} onChange={(e) => setManualFields({ ...manualFields, statement_date: e.target.value })} className="mt-1.5" data-testid="stmt-manual-date" />
                                        </div>
                                        <div>
                                            <Label className="eyebrow">Due Date</Label>
                                            <Input type="date" value={manualFields.due_date} onChange={(e) => setManualFields({ ...manualFields, due_date: e.target.value })} className="mt-1.5" data-testid="stmt-manual-due" />
                                        </div>
                                        <div>
                                            <Label className="eyebrow">Total Due</Label>
                                            <Input type="number" step="0.01" value={manualFields.total_amount_due} onChange={(e) => setManualFields({ ...manualFields, total_amount_due: e.target.value })} className="mt-1.5" data-testid="stmt-manual-total" />
                                        </div>
                                        <div>
                                            <Label className="eyebrow">Min Due</Label>
                                            <Input type="number" step="0.01" value={manualFields.min_amount_due} onChange={(e) => setManualFields({ ...manualFields, min_amount_due: e.target.value })} className="mt-1.5" />
                                        </div>
                                    </div>
                                )}
                                <Button type="submit" disabled={uploading} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="stmt-upload-submit">
                                    {uploading ? (<><Loader2 className="animate-spin mr-2" size={14} />Uploading{runOcr ? " & extracting" : ""}…</>) : "Upload"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                    </div>
                )}
            </div>

            {hasRole("admin", "finance") && <GmailSyncPanel onSynced={load} />}

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <Select value={cardFilter} onValueChange={setCardFilter}>
                    <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All cards</SelectItem>
                        {cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Input type="month" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="w-[200px]" />
                {monthFilter && (
                    <Button variant="outline" size="sm" onClick={() => setMonthFilter("")}>Clear month</Button>
                )}
            </div>

            <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="dense-table w-full">
                        <thead>
                            <tr>
                                <th>Month</th>
                                <th>Card</th>
                                <th>Statement Date</th>
                                <th>Due Date</th>
                                <th className="text-right">Opening</th>
                                <th className="text-right">Closing</th>
                                <th className="text-right">Total Due</th>
                                <th className="text-right">Min Due</th>
                                <th className="text-right">Purchases</th>
                                <th className="text-right">Payments</th>
                                <th>File</th>
                                <th>Status</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((s) => (
                                <tr key={s.id} data-testid={`stmt-row-${s.id}`}>
                                    <td className="tabular-nums">{s.statement_month}</td>
                                    <td>{cardMap[s.card_id]?.bank_name || s.bank_name} · •••• {s.last_4}</td>
                                    <td className="tabular-nums">{fmtDate(s.statement_date)}</td>
                                    <td className="tabular-nums">{fmtDate(s.due_date)}</td>
                                    <td className="text-right tabular-nums text-stone-500" data-testid={`stmt-opening-${s.id}`}>{fmtMoney(s.opening_balance)}</td>
                                    <td className="text-right tabular-nums font-medium" data-testid={`stmt-closing-${s.id}`}>{fmtMoney(s.closing_balance)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(s.total_amount_due)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(s.min_amount_due)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(s.purchases)}</td>
                                    <td className="text-right tabular-nums">{fmtMoney(s.payments)}</td>
                                    <td>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {s.file_url && (
                                                <a href={`${BACKEND_URL}${s.file_url}`} target="_blank" rel="noreferrer" className="text-stone-700 hover:text-stone-900 inline-flex items-center gap-1">
                                                    <FileText size={14} /> PDF <ExternalLink size={11} />
                                                </a>
                                            )}
                                            {cardMap[s.card_id]?.has_statement_password && (
                                                <StatementPasswordReveal cardId={s.card_id} size="xs" label="Password" />
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        <button onClick={() => setReview(s)} className="text-xs underline text-stone-700 hover:text-stone-900" data-testid={`stmt-review-${s.id}`}>
                                            {s.status || "Uploaded"}
                                        </button>
                                    </td>
                                    <td>
                                        <div className="flex items-center justify-end gap-1">
                                            <Link
                                                to={`/statements/${s.id}/ledger`}
                                                title="View ledger"
                                                className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900"
                                                data-testid={`stmt-ledger-${s.id}`}
                                            >
                                                <BookOpen size={13} />
                                            </Link>
                                            <button onClick={() => setReview(s)} title="Edit" className="p-1.5 rounded hover:bg-stone-100 text-stone-500 hover:text-stone-900" data-testid={`stmt-edit-${s.id}`}>
                                                <FileText size={13} />
                                            </button>
                                            {hasRole("admin") && (
                                                <button onClick={() => del(s.id)} title="Delete" className="p-1.5 rounded hover:bg-red-50 text-stone-500 hover:text-red-600" data-testid={`stmt-delete-${s.id}`}>
                                                    <Trash2 size={13} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={13} className="p-0"><EmptyState
                                    icon={FileText}
                                    title="No statements uploaded yet."
                                    hint="Upload your first statement PDF — VAULT will OCR it, detect the closing balance, and reconcile expenses in seconds."
                                    cta={{ to: "/email-capture", label: "Upload statement", testId: "empty-statements-cta" }}
                                    className="rounded-none border-0 py-12"
                                /></td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* OCR review dialog */}
            <Dialog open={!!review} onOpenChange={(v) => !v && setReview(null)}>
                <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">Statement review</DialogTitle>
                    </DialogHeader>
                    {review && (
                        <div className="space-y-4">
                            {review.ocr_data?.error && (
                                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                    OCR failed: {review.ocr_data.error}. Fill fields manually.
                                </div>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {[
                                    ["Statement Date", "statement_date", "date"],
                                    ["Period From", "period_from", "date"],
                                    ["Period To", "period_to", "date"],
                                    ["Due Date", "due_date", "date"],
                                    ["Total Amount Due", "total_amount_due", "number"],
                                    ["Min Amount Due", "min_amount_due", "number"],
                                    ["Opening Balance", "opening_balance", "number"],
                                    ["Closing Balance", "closing_balance", "number"],
                                    ["Purchases", "purchases", "number"],
                                    ["Payments", "payments", "number"],
                                    ["Interest", "interest", "number"],
                                    ["Late Fee", "late_fee", "number"],
                                    ["GST", "gst", "number"],
                                    ["Reward Earned", "reward_points_earned", "number"],
                                    ["Reward Redeemed", "reward_points_redeemed", "number"],
                                ].map(([lbl, k, type]) => (
                                    <div key={k}>
                                        <Label className="eyebrow">{lbl}</Label>
                                        <Input
                                            type={type}
                                            value={review[k] || (type === "number" ? 0 : "")}
                                            onChange={(e) => setReview({ ...review, [k]: e.target.value })}
                                            className="mt-1.5"
                                        />
                                    </div>
                                ))}
                            </div>
                            {review.transactions?.length > 0 && (
                                <div>
                                    <div className="flex items-center justify-between">
                                        <Label className="eyebrow">Statement-period ledger</Label>
                                        <Link
                                            to={`/statements/${review.id}/ledger`}
                                            className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
                                            data-testid="stmt-open-ledger-page"
                                        >
                                            <BookOpen size={11} /> Open full ledger page →
                                        </Link>
                                    </div>
                                    <div className="mt-2">
                                        <StatementLedger statementId={review.id} variant="compact" />
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={() => setReview(null)}>Close</Button>
                                <Button className="bg-stone-900 hover:bg-stone-800 text-white" onClick={saveReview} data-testid="stmt-save-review">Save reviewed data</Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
